import {
  AHListItem,
  AHSeparator,
  AHText,
  AHTextFonts,
  Arrow,
  Box,
  ColorType,
  useLayoutContext,
  useTheme,
} from "arkh";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";

import { EmptyResult } from "widgets";
import { FlatList } from "react-native";
import { FormattedMessage } from "react-intl";
import React, { ReactNode, useCallback } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { WidgetRoutinePositivePercentageScreenQuery } from "./__generated__/WidgetRoutinePositivePercentageScreenQuery.graphql";
import { WidgetRoutinePositivePercentageScreen_routine$key } from "./__generated__/WidgetRoutinePositivePercentageScreen_routine.graphql";
export function WidgetRoutinePositivePercentageScreen(
  props: UserLoggedInNavigatorScreenProps<"WidgetRoutinePositivePercentage">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWidgetRoutinePositivePercentageScreen
          {...props}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWidgetRoutinePositivePercentageScreen({
  route,
  navigation,
  fetchKey,
}: UserLoggedInNavigatorScreenProps<"WidgetRoutinePositivePercentage"> & {
  fetchKey: string;
}) {
  const { space } = useLayoutContext();
  const safeAreaInsets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const data = useLazyLoadQuery<WidgetRoutinePositivePercentageScreenQuery>(
    graphql`
      query WidgetRoutinePositivePercentageScreenQuery($widgetId: ID!) {
        widgetRoutinePositivePercentage(id: $widgetId) {
          id
          title
          routines {
            id
            ...WidgetRoutinePositivePercentageScreen_routine
          }
        }
      }
    `,
    {
      widgetId: route.params.widgetId,
    },
    {
      fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const widget = data.widgetRoutinePositivePercentage;
  const listData = widget?.routines ?? [];
  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <Item
          routine={item}
          navigation={navigation}
          isTopItem={index === 0}
          isBottomItem={listData.length - 1 === index}
        />
      );
    },
    [listData.length, navigation]
  );

  return (
    <FlatList
      ItemSeparatorComponent={AHSeparator}
      contentContainerStyle={{
        padding: space.large,
        paddingBottom: space.large + (isCompact ? safeAreaInsets.bottom : 0),
      }}
      data={listData}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      ListFooterComponent={
        <Box style={{ flexDirection: "column" }} spaceTop="large">
          <Box
            spaceBottom="xsmall"
            style={{
              height: 40,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Box
              style={{
                width: 50,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AHText type="title" font={AHTextFonts.BOLDER}>
                %
              </AHText>
            </Box>
            <AHText
              type="body"
              style={{
                flex: 1,
              }}
            >
              <FormattedMessage
                defaultMessage="Percentage of Yes/No tasks that has been answered Yes."
                description="routine positive percentage screen percentage explanation"
              />
            </AHText>
          </Box>
          <Box
            style={{
              height: 40,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Box
              style={{
                width: 50,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmptyResult size="medium" />
            </Box>
            <AHText
              type="body"
              style={{
                flex: 1,
              }}
            >
              <FormattedMessage
                defaultMessage="Checklist does not have any Yes/No tasks."
                description="routine positive percentage screen empty result explanation"
              />
            </AHText>
          </Box>
        </Box>
      }
    />
  );
}

const ResultTitle: React.FC<React.PropsWithChildren<{ color: ColorType }>> = ({
  children,
  color,
}) => {
  const theme = useTheme();
  return (
    <AHText
      numberOfLines={3}
      ellipsizeMode="tail"
      adjustsFontSizeToFit
      minimumFontScale={0.7}
      font={AHTextFonts.BOLD}
      style={{ color: theme[color], paddingLeft: 8, flex: 1 }}
    >
      {children}
    </AHText>
  );
};

const ResultBlock = ({ children }: { children?: ReactNode | undefined }) => {
  return (
    <Box
      style={{
        width: 34,
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      {children}
    </Box>
  );
};

function Item(props: {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetRoutinePositivePercentage">;
  isBottomItem?: boolean;
  isTopItem?: boolean;
  routine: WidgetRoutinePositivePercentageScreen_routine$key;
}) {
  const routine = useFragment(
    graphql`
      fragment WidgetRoutinePositivePercentageScreen_routine on Routine {
        id
        title
        inProgressOccasion {
          id
          sequenceNumber
        }
        lastCompletedOccasion {
          id
          sequenceNumber
          percentage
        }
      }
    `,
    props.routine
  );

  return (
    <AHListItem
      onPress={() => {
        if (routine.inProgressOccasion) {
          props.navigation.replace("RoutineOccasion", {
            routineId: routine.id,
            sequenceNumber: routine.inProgressOccasion.sequenceNumber,
            isNotFeedOrigin: true,
          });
        } else if (routine.lastCompletedOccasion) {
          props.navigation.replace("RoutineOccasion", {
            routineId: routine.id,
            sequenceNumber: routine.lastCompletedOccasion.sequenceNumber,
            isNotFeedOrigin: true,
          });
        }
      }}
      isTopItem={props.isTopItem}
      isBottomItem={props.isBottomItem}
      key={routine.id}
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <ResultBlock>
        {routine.lastCompletedOccasion?.percentage === null ||
        routine.lastCompletedOccasion?.percentage === undefined ? (
          <EmptyResult size="small" />
        ) : (
          <AHText
            type="subtitle"
            font={AHTextFonts.BOLDER}
            color={
              routine.lastCompletedOccasion?.percentage >= 80
                ? "positive"
                : "negative"
            }
            style={{
              opacity: 1,
            }}
          >
            {`${routine.lastCompletedOccasion.percentage}%`}
          </AHText>
        )}
      </ResultBlock>
      <ResultTitle
        color={
          routine.inProgressOccasion?.id === routine.lastCompletedOccasion?.id
            ? "darkerText"
            : "washedText"
        }
      >
        {routine.title}
      </ResultTitle>
      <Box style={{ flexDirection: "row", alignItems: "center" }}>
        <Arrow />
      </Box>
    </AHListItem>
  );
}
